import { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { MapPin, Wifi, Utensils, Shield, Zap, Home as HomeIcon, Bed, Loader2, ArrowLeft, X, ChevronLeft, ChevronRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Image Lightbox Component
const ImageLightbox = ({ images, currentIndex, onClose, onPrev, onNext }) => {
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') onPrev();
      if (e.key === 'ArrowRight') onNext();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose, onPrev, onNext]);

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center"
        onClick={onClose}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-white hover:text-gray-300 z-50"
        >
          <X size={32} />
        </button>

        {/* Previous button */}
        <button
          onClick={(e) => { e.stopPropagation(); onPrev(); }}
          className="absolute left-4 text-white hover:text-gray-300 bg-black/50 p-2 rounded-full z-50"
        >
          <ChevronLeft size={32} />
        </button>

        {/* Image */}
        <motion.img
          key={currentIndex}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          src={images[currentIndex]}
          alt={`Image ${currentIndex + 1}`}
          className="max-h-[90vh] max-w-[90vw] object-contain rounded-lg"
          onClick={(e) => e.stopPropagation()}
        />

        {/* Next button */}
        <button
          onClick={(e) => { e.stopPropagation(); onNext(); }}
          className="absolute right-4 text-white hover:text-gray-300 bg-black/50 p-2 rounded-full z-50"
        >
          <ChevronRight size={32} />
        </button>

        {/* Image counter */}
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white bg-black/50 px-4 py-2 rounded-full font-body">
          {currentIndex + 1} / {images.length}
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

// Horizontal Image Gallery Component
const ImageGallery = ({ images, propertyName }) => {
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [mainImage, setMainImage] = useState(0);
  const scrollRef = useRef(null);

  const openLightbox = (index) => setSelectedIndex(index);
  const closeLightbox = () => setSelectedIndex(null);
  
  const goToPrev = () => {
    setSelectedIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };
  
  const goToNext = () => {
    setSelectedIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  const scrollGallery = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = 200;
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="space-y-4">
      {/* Main Image */}
      <div 
        className="relative cursor-pointer group"
        onClick={() => openLightbox(mainImage)}
      >
        <img
          src={images[mainImage]}
          alt={propertyName}
          className="w-full h-96 object-cover rounded-2xl transition-transform group-hover:scale-[1.01]"
          data-testid="property-main-image"
        />
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-all rounded-2xl flex items-center justify-center pointer-events-none">
          <span className="opacity-0 group-hover:opacity-100 bg-white/90 text-gray-800 px-4 py-2 rounded-full font-body text-sm transition-opacity">
            Click to enlarge
          </span>
        </div>
      </div>

      {/* Thumbnail Gallery with Horizontal Scroll */}
      {images.length > 1 && (
        <div className="relative">
          {/* Left scroll button */}
          <button
            onClick={() => scrollGallery('left')}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white shadow-lg p-2 rounded-full -ml-3"
          >
            <ChevronLeft size={20} className="text-gray-700" />
          </button>

          {/* Scrollable thumbnail container */}
          <div
            ref={scrollRef}
            className="flex gap-3 overflow-x-auto scrollbar-hide py-2 px-8"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {images.map((img, idx) => (
              <div
                key={idx}
                onClick={() => setMainImage(idx)}
                className={`flex-shrink-0 cursor-pointer rounded-xl overflow-hidden transition-all ${
                  mainImage === idx 
                    ? 'ring-2 ring-brand-purple ring-offset-2' 
                    : 'hover:opacity-80'
                }`}
              >
                <img
                  src={img}
                  alt={`${propertyName} ${idx + 1}`}
                  className="w-28 h-20 object-cover"
                />
              </div>
            ))}
          </div>

          {/* Right scroll button */}
          <button
            onClick={() => scrollGallery('right')}
            className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white shadow-lg p-2 rounded-full -mr-3"
          >
            <ChevronRight size={20} className="text-gray-700" />
          </button>

          {/* Image count badge */}
          <div className="absolute bottom-2 right-2 bg-black/70 text-white px-3 py-1 rounded-full text-xs font-body">
            {images.length} photos
          </div>
        </div>
      )}

      {/* Lightbox */}
      {selectedIndex !== null && (
        <ImageLightbox
          images={images}
          currentIndex={selectedIndex}
          onClose={closeLightbox}
          onPrev={goToPrev}
          onNext={goToNext}
        />
      )}
    </div>
  );
};

export const Properties = () => {
  const { id } = useParams();
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (id) {
      fetchProperty(id);
    } else {
      fetchAllProperties();
    }
  }, [id]);

  const fetchAllProperties = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/properties?per_page=100`);
      setProperties(response.data.properties);
    } catch (error) {
      console.error('Error fetching properties:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProperty = async (propertyId) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/properties/${propertyId}`);
      setSelectedProperty(response.data);
    } catch (error) {
      console.error('Error fetching property:', error);
    } finally {
      setLoading(false);
    }
  };

  const getAmenityIcon = (amenity) => {
    const lowerAmenity = amenity.toLowerCase();
    if (lowerAmenity.includes('wifi') || lowerAmenity.includes('internet')) return Wifi;
    if (lowerAmenity.includes('food') || lowerAmenity.includes('meal')) return Utensils;
    if (lowerAmenity.includes('security') || lowerAmenity.includes('cctv')) return Shield;
    if (lowerAmenity.includes('power') || lowerAmenity.includes('backup')) return Zap;
    if (lowerAmenity.includes('furnish')) return HomeIcon;
    return Bed;
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-16 flex justify-center items-center" data-testid="properties-loading">
        <Loader2 className="animate-spin text-brand-purple" size={48} />
      </div>
    );
  }

  if (id && selectedProperty) {
    return (
      <div className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <Link
            to="/properties"
            className="inline-flex items-center gap-2 text-brand-purple font-body font-medium mb-8 hover:gap-3 transition-all"
            data-testid="property-back-button"
          >
            <ArrowLeft size={20} />
            Back to Properties
          </Link>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
            {/* Image Gallery */}
            <ImageGallery 
              images={selectedProperty.images} 
              propertyName={selectedProperty.name} 
            />

            <div>
              <h1 className="font-heading text-4xl font-bold text-gray-900 mb-4" data-testid="property-name">
                {selectedProperty.name}
              </h1>
              
              <div className="flex items-center gap-2 text-gray-600 mb-6">
                <MapPin size={20} className="text-brand-purple" />
                <span className="font-body" data-testid="property-location">{selectedProperty.area}</span>
              </div>

              <p className="text-gray-600 font-body leading-relaxed mb-6" data-testid="property-description">
                {selectedProperty.description || 'Experience comfortable living in this well-maintained property with all modern amenities.'}
              </p>

              <div className="bg-gray-50 p-6 rounded-2xl mb-6">
                <h3 className="font-heading text-xl font-semibold text-gray-900 mb-4">Room Types & Pricing</h3>
                <div className="space-y-3">
                  {Object.entries(selectedProperty.pricing).map(([type, price]) => {
                    const roomTypeLabels = {
                      single: 'Single Sharing',
                      double: 'Double Sharing',
                      triple: 'Triple Sharing',
                      quadruple: 'Quadruple/4-Sharing'
                    };
                    return price > 0 && (
                      <div key={type} className="flex justify-between items-center" data-testid={`pricing-${type}`}>
                        <span className="font-body text-gray-700">{roomTypeLabels[type] || type}</span>
                        <span className="font-heading text-xl font-semibold text-brand-purple">₹{price}/month</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              <Link
                to="/inquiry"
                data-testid="property-enquire-button"
                className="block w-full bg-brand-purple text-white text-center py-4 rounded-full font-body text-lg font-medium hover:bg-opacity-90 transition-all shadow-lg shadow-purple-900/20"
              >
                Enquire for This Property
              </Link>
            </div>
          </div>

          <div className="mb-12">
            <h2 className="font-heading text-3xl font-semibold text-gray-900 mb-8">Amenities</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {selectedProperty.amenities.map((amenity, index) => {
                const AmenityIcon = getAmenityIcon(amenity);
                return (
                  <div
                    key={index}
                    className="flex items-center gap-4 bg-gray-50 p-6 rounded-xl"
                    data-testid={`amenity-${index}`}
                  >
                    <div className="w-12 h-12 bg-brand-purple/10 rounded-xl flex items-center justify-center">
                      <AmenityIcon className="text-brand-purple" size={24} />
                    </div>
                    <span className="font-body text-gray-700">{amenity}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {selectedProperty.map_url && (
            <div>
              <h2 className="font-heading text-3xl font-semibold text-gray-900 mb-8">Location</h2>
              <div className="w-full h-96 bg-gray-100 rounded-2xl overflow-hidden" data-testid="property-map">
                <iframe
                  src={selectedProperty.map_url}
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title={`Map of ${selectedProperty.name}`}
                ></iframe>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="pt-16">
      <section className="bg-gradient-to-br from-white via-purple-50/30 to-white py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h1 className="font-heading text-5xl md:text-7xl font-bold tracking-tight text-gray-900 mb-6">
              Our <span className="text-brand-purple">Properties</span>
            </h1>
            <p className="text-lg md:text-xl leading-relaxed text-gray-600 font-body max-w-3xl mx-auto">
              Explore our range of comfortable and affordable accommodations
            </p>
          </motion.div>

          {properties.length === 0 ? (
            <div className="text-center py-20" data-testid="properties-empty">
              <p className="text-gray-600 font-body text-lg">
                No properties available at the moment. Please check back later.
              </p>
            </div>
          ) : (
            <div className="space-y-20">
              {properties.map((property, index) => (
                <motion.div
                  key={property.id}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                  viewport={{ once: true }}
                  className="bg-white rounded-2xl overflow-hidden shadow-lg border border-gray-100"
                  data-testid={`property-section-${property.id}`}
                >
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 p-8">
                    <div>
                      <img
                        src={property.images[0]}
                        alt={property.name}
                        className="w-full h-80 object-cover rounded-xl"
                      />
                    </div>

                    <div>
                      <h2 className="font-heading text-3xl font-bold text-gray-900 mb-3">
                        {property.name}
                      </h2>
                      <div className="flex items-center gap-2 text-gray-600 mb-6">
                        <MapPin size={18} className="text-brand-purple" />
                        <span className="font-body">{property.area}</span>
                      </div>

                      <div className="mb-6">
                        <h3 className="font-heading text-lg font-semibold text-gray-900 mb-3">Room Types</h3>
                        <div className="flex flex-wrap gap-2">
                          {property.room_types.map((type, idx) => (
                            <span
                              key={idx}
                              className="bg-brand-orange/10 text-brand-orange px-4 py-2 rounded-full font-body text-sm font-medium"
                            >
                              {type}
                            </span>
                          ))}
                        </div>
                      </div>

                      <div className="mb-6">
                        <h3 className="font-heading text-lg font-semibold text-gray-900 mb-3">Amenities</h3>
                        <div className="grid grid-cols-2 gap-2">
                          {property.amenities.slice(0, 6).map((amenity, idx) => (
                            <div key={idx} className="flex items-center gap-2">
                              <div className="w-2 h-2 bg-brand-purple rounded-full"></div>
                              <span className="font-body text-sm text-gray-700">{amenity}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      <Link
                        to={`/properties/${property.id}`}
                        data-testid={`view-property-button-${property.id}`}
                        className="block w-full bg-brand-purple text-white text-center py-3 rounded-full font-body font-medium hover:bg-opacity-90 transition-all"
                      >
                        View Full Details
                      </Link>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};
